import { Location } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { GroupBy } from 'src/app/core/_enums/groupby';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { Combustible } from 'src/app/core/_models/combustible';
import { AuthService } from 'src/app/core/_services/auth.service';
import { CombustibleService } from 'src/app/core/_services/combustible.service';
import { DredgingService } from 'src/app/core/_services/dredging.service';
import { ResourceListRefreshService } from 'src/app/core/_services/resource.list.refresh.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { UtilsService } from 'src/app/core/_utils/utils.service';
import { UpdateResourceAction } from 'src/app/store/actions/resource.actions';
import { AppState } from 'src/app/store/app.state';
import { GlobalConstants } from 'src/constants/global.constants';
import { environment } from 'src/environments/environment';
import { DialogDuplicateCodeComponent } from '../shared/dialog-duplicate-code/dialog-duplicate-code.component';
import { DialogOverviewComponent } from '../shared/dialog-overview/dialog-overview.component';

@Component({
  selector: 'app-dredging-fuel',
  templateUrl: './dredging-fuel.component.html',
  styleUrls: ['./dredging-fuel.component.scss']
})
export class DredgingFuelComponent implements OnInit {
  state: any = {
    perimeterId: null
  };

  currentItem: Combustible = {};
  form!: FormGroup;
  favoriteId?: string;

  fileToUpload?: File;
  justificationFilePath: string;

  @ViewChild('fileInput', { static: false })
  fileInput!: ElementRef;

  constructor(
    private activeRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private dredgingService: DredgingService,
    private combustibleService: CombustibleService,
    private dialog: MatDialog,
    private router: Router,
    private snackbarService: SnackbarService,
    private store: Store<AppState>,
    private location: Location,
    private utilsService: UtilsService,
    private uploadService: UploadService,
    private authService: AuthService,
    private spinnerService: SpinnerOverlayService,
    private resourceListRefreshService: ResourceListRefreshService
  ) {
    this.justificationFilePath = environment.baseUrl + 'Documents/Justification/';
  }

  ngOnInit(): void {
    this.form = this.formBuilder?.group({
      code: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(9)]],
      displayName: ['', Validators.required],
      environmentalFactor: ['', Validators.required],
      tCo2PerLiter: ['', Validators.required],
      fileName: [],
      fileDescription: [],
      versionId: []
    });

    this.activeRoute.params.subscribe((params) => {
      const id = params['id'];
      if (!id || id === '0') {
        this.initForm(this.form, {});
        return;
      }
      this.dredgingService.get(id).subscribe({
        next: (data) => this.initForm(this.form, data),
        error: () => this.initForm(this.form, {})
      });
    });
  }

  isFormDisabled() {
    if (!this.currentItem?.resourceId) return false;
    const perimeterId = this.getPerimeterId();
    const ownerId = this.currentItem?.ownerId;
    const isRuadeResource = !ownerId || ownerId === GlobalConstants.EMPTY_GUID;
    if (isRuadeResource) {
      return perimeterId !== GlobalConstants.EMPTY_GUID;
    }
    return perimeterId !== ownerId;
  }

  private getPerimeterId(): string {
    if (history.state.perimeterId) {
      this.state.perimeterId = history.state.perimeterId;
      return history.state.perimeterId;
    }

    if (this.authService.roleValue?.perimeterId) {
      this.state.perimeterId = this.authService.roleValue.perimeterId;
      return this.authService.roleValue.perimeterId;
    }

    return this.state.perimeterId || '';
  }

  initForm(form: FormGroup, currentItem: Combustible) {
    this.currentItem = currentItem;
    if (this.currentItem && this.currentItem.resourceId) {
      this.utilsService.getFavoriteIdForResource(this.currentItem.resourceId).then((res) => {
        this.favoriteId = res ? res.userFavoriteId : '';
      });
    }

    form.reset();
    const ci: any = currentItem;
    form.patchValue({
      ...currentItem,
      environmentalFactor: ci.coefficientMJ ?? currentItem.environmentalData?.coefficientMJ ?? currentItem.environmentalFactor,
      tCo2PerLiter: ci.coefficientCO2 ?? currentItem.environmentalData?.coefficientCO2 ?? currentItem.tCo2PerLiter
    });
  }

  get formCrls() {
    return this.form?.controls;
  }

  clickduplicate(resourceId: string) {
    this.dialog
      .open(DialogDuplicateCodeComponent, { data: { code: this.currentItem.code, maxLength: 9, duplicate: (code: string) => this.dredgingService.add({ ...this.currentItem, code }) } })
      .afterClosed()
      .subscribe((res) => {
        if (res !== undefined) this.majStore(SnackbarTypes.duplicate);
      });
  }

  clickDelete(resourceId: string) {
    let title = 'Etes-vous sûr de vouloir supprimer ce combustible ?';

    const dialogRef = this.dialog.open(DialogOverviewComponent, {
      data: { title: title }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteItem(resourceId);
      }
    });
  }

  deleteItem(resourceId: string) {
    if (resourceId) {
      this.dredgingService.delete(resourceId).subscribe({
        next: () => {
          this.majStore(SnackbarTypes.delete);
          this.router.navigate([SiteMapUrl.MenuEnergie]);
        },
        error: (error) => {
          this.snackbarService.open(SnackbarTypes.error);
          console.error(error);
        }
      });
    }
  }

  majStore(snackbarMsgType: string) {
    this.combustibleService.getDataSourceObservable(GroupBy.family).subscribe(
      (arr) => {
        if (arr) {
          this.store.dispatch(new UpdateResourceAction(arr));
          this.snackbarService.open(snackbarMsgType);
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
      }
    );

    this.resourceListRefreshService.triggerRefresh();
  }

  async clickSave(currentItem: Combustible) {
    if (this.form.invalid) return;

    const mjValue = this.form.get('environmentalFactor')?.value;
    const co2Value = this.form.get('tCo2PerLiter')?.value;
    const mjNum = mjValue !== '' && mjValue != null ? Number(mjValue) : null;
    const co2Num = co2Value !== '' && co2Value != null ? Number(co2Value) : null;

    currentItem.code = this.form.get('code')?.value;
    currentItem.displayName = this.form.get('displayName')?.value;
    currentItem.environmentalFactor = mjValue;
    currentItem.tCo2PerLiter = co2Value;
    (currentItem as any).coefficientMJ = mjNum;
    (currentItem as any).coefficientCO2 = co2Num;
    const envData: any = {
      coefficientMJ: mjNum,
      coefficientCO2: co2Num
    };
    if (currentItem.environmentalData?.resourceId) envData.resourceId = currentItem.environmentalData.resourceId;
    if (currentItem.environmentalData?.environmentalDataId) envData.environmentalDataId = currentItem.environmentalData.environmentalDataId;
    if (currentItem.ruade && currentItem.environmentalData?.versionId) envData.versionId = currentItem.environmentalData.versionId;
    currentItem.environmentalData = envData;
    currentItem.fileDescription = this.form.get('fileDescription')?.value;
    currentItem.fileName = this.form.get('fileName')?.value;
    if (!currentItem.ruade) delete (currentItem as any).versionId;

    if (!this.currentItem?.resourceId) {
      delete currentItem.resourceId;
      this.dredgingService.add(currentItem).subscribe(
        (data: any) => {
          const newResourceId = typeof data === 'string' ? data : data?.resourceId;
          this.currentItem = { ...currentItem, resourceId: newResourceId };
          this.majStore(SnackbarTypes.add);
          this.router.navigate([SiteMapUrl.MenuDredgingFuel, newResourceId], { state: { currentItem: this.currentItem, perimeterId: this.getPerimeterId() } });
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    } else {
      this.dredgingService.update(currentItem).subscribe(
        (data: Combustible) => {
          this.currentItem = data;
          this.majStore(SnackbarTypes.update);
          this.location.replaceState(`${SiteMapUrl.MenuDredgingFuel}/${data.resourceId}`);
        },
        (error) => {
          this.snackbarService.openError(error);
        }
      );
    }

    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  addToFavorite(resourceId: string) {
    this.utilsService.addToFavorite(resourceId).then((res) => {
      this.favoriteId = res;
    });
  }

  removeFromFavorite() {
    if (this.favoriteId) {
      this.utilsService.removeFromFavorite(this.favoriteId).then(() => {
        this.favoriteId = '';
      });
    }
  }

  uploadProof(resourceId?: string, files?: any) {
    if (!resourceId || !files || files.length === 0) {
      return;
    }

    this.fileToUpload = <File>files[0];
    const formData = new FormData();
    formData.append('file', this.fileToUpload, `${resourceId}.${this.fileToUpload.name.split('.').pop()}`);

    this.spinnerService.show();
    this.uploadService.uploadProof(resourceId, formData).subscribe(
      (event: any) => {
        if (event.type === HttpEventType.Response) {
          this.spinnerService.hide();
          this.form.get('fileDescription')?.setValue(event?.body);
          this.form.get('fileName')?.setValue(this.fileToUpload?.name);
          this.fileInput.nativeElement.value = '';
        }
      },
      (error) => {
        this.snackbarService.open(SnackbarTypes.error);
        this.spinnerService.hide();
      }
    );
  }

  deleteFile() {
    this.fileInput.nativeElement.value = '';
    this.form.get('fileName')?.reset();
    this.form.get('fileDescription')?.reset();
  }
}
